import { API } from "taskpane/constants/api";
import {
  IConferenceQueryResult,
  IConferenceRoles,
  IConferenceAccessSettings,
  IQueryResponse,
  IConferenceStatusResponse,
} from "taskpane/models/interfaces";
import useQueryStartSession, { IStartSession, IStartSessionResponse } from "./useQueryStartSession";
import useQuery from "./useQuery";
import { getRandomRequestId, getRolesBySettings } from "taskpane/utils";
import useQueryEndSession from "./useQueryEndSession";
import { ConferenceStatusCode, QueryType } from "taskpane/models/enums";
import { OnReady } from "taskpane/models/types";
import { getQueryErrorDeauthorization, getQueryErrorTryAgainLater } from "taskpane/resources";

interface ICreateConferencePostParams {
  reqId: string;
  aimsid: string;
  params: {
    name: string;
    type: string;
    participants: [];
    callParticipants: boolean;
    roles?: IConferenceRoles;
  };
}

export interface ICreateConference extends IStartSession {
  apiVersion: number;
  settings: IConferenceAccessSettings;
}

export interface ICreateConferenceResponse extends IConferenceStatusResponse {
  results: {
    conferenceId: string;
    conferenceUrl: string;
  };
}

const useQueryCreateConference = (
  params: ICreateConference,
  onReady: OnReady<IConferenceQueryResult<IStartSessionResponse | ICreateConferenceResponse>>
): void => {
  const { token, email, apiUrl, apiVersion, settings } = params;
  const startSessionParams: IStartSession = {
    token,
    email,
    apiUrl,
  };

  useQueryStartSession(startSessionParams, (responseStartSession: IConferenceQueryResult<IStartSessionResponse>) => {
    if (responseStartSession.errorMessage) {
      return onReady(responseStartSession);
    }

    const aimsid: string = responseStartSession.response.response.data.aimsid;
    const url: string = `${apiUrl}/rapi/conference/create`;
    const postParams: ICreateConferencePostParams = {
      reqId: getRandomRequestId(),
      aimsid: aimsid,
      params: {
        name: email,
        type: "equitable",
        participants: [],
        callParticipants: false,
      },
    };

    if (apiVersion >= API.versionWithSettings) {
      postParams["params"]["roles"] = getRolesBySettings(settings);
    }

    useQuery(
      url,
      "POST",
      (result: IQueryResponse<ICreateConferenceResponse>) => {
        const response: IConferenceQueryResult<ICreateConferenceResponse> = {
          queryType: QueryType.createConference,
          ...result,
        };

        if (result.errorMessage) {
          return onReady(response);
        }

        const statusCode = response.response.status.code;

        if (statusCode === ConferenceStatusCode.notAuthorized) {
          return onReady({
            ...response,
            errorMessage: getQueryErrorDeauthorization(),
          });
        } else if (statusCode !== ConferenceStatusCode.succeeded) {
          return onReady({
            ...response,
            errorMessage: getQueryErrorTryAgainLater("при создании конференции"),
          });
        }

        useQueryEndSession({
          aimsid: aimsid,
          apiUrl: apiUrl,
        });

        onReady(response);
      },
      { data: postParams }
    );
  });
};

export default useQueryCreateConference;
